import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';

const kpis = [
    { title: 'Outpatient', value: '142', change: '12%', changeType: 'up' },
    { title: 'Avg visits per day', value: '8', unit: 'visits', change: '5%', changeType: 'up' },
];

const followUpBars = [
    { label: 'Completed', value: 24 },
    { label: 'Scheduled', value: 12 },
    { label: 'Overdue', value: 5 },
];

const outpatientTimeData = [
    { name: 'Jan', value: 12 },
    { name: 'Feb', value: 5 },
    { name: 'Mar', value: 11 },
    { name: 'Apr', value: 20 },
    { name: 'May', value: 4 },
    { name: 'Jun', value: 5 },
    { name: 'Jul', value: 8 },
    { name: 'Aug', value: 9 },
    { name: 'Sep', value: 12 },
    { name: 'Oct', value: 2 },
    { name: 'Nov', value: 14 },
    { name: 'Dec', value: 4 },
];

const visitByDayData = [
    { name: 'Saturday', visit: 2 },
    { name: 'Sunday', visit: 4 },
    { name: 'Monday', visit: 6 },
    { name: 'Tuesday', visit: 3 },
    { name: 'Wednesday', visit: 3 },
    { name: 'Thursday', visit: 10 },
    { name: 'Friday', visit: 14 },
];

const shareByType = [
    { name: 'consultation', value: 25 },
    { name: 'follow up', value: 15 },
    { name: 'vaccination', value: 12 },
    { name: 'diagnostics', value: 18 },
    { name: 'procedure', value: 8 },
    { name: 'treatment', value: 10 },
    { name: 'emergency', value: 7 },
    { name: 'surgery', value: 5 },
];

const PIE_COLORS = ['#0f4c5c', '#f97316', '#10b981', '#06b6d4', '#d946ef', '#22c55e', '#1e3a5f', '#c2410c'];

const OutpatientCare = ({ onBack }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader title="Outpatient Care" subtitle="2026" onBack={onBack} />

            {/* Main 2-column layout */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Left column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Key metrics — at TOP of left column */}
                    <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                        <div className="clinic-dashboard-section-title">Key metrics</div>
                        <div className="clinic-dashboard-kpi-row two">
                            {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                        </div>
                    </div>

                    {/* Status of the follow up — with flex so bars render */}
                    <div className="clinic-dashboard-section-card" style={{ display: 'flex', flexDirection: 'column', animationDelay: '80ms' }}>
                        <div className="clinic-dashboard-section-title">Status of the follow up</div>
                        <div className="clinic-dashboard-catbar-list large">
                            {followUpBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    </div>

                    {/* Outpatient visit by Day — bar chart at BOTTOM of left */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient visit by Day</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '180px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={visitByDayData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar animationBegin={600} dataKey="visit" fill="#0f4c5c" radius={[4, 4, 0, 0]} name="visit" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Outpatient over time — line chart */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient over time</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={outpatientTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Line animationBegin={600} type="monotone" dataKey="value" stroke="#0f4c5c" strokeWidth={2} dot={{ r: 4, fill: '#0f4c5c' }} activeDot={{ r: 6 }} name="outpatient" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Outpatient share by type — pie */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '320ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient share by type</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <ResponsiveContainer width="100%" height="70%">
                                <PieChart>
                                    <Pie
                                        data={shareByType}
                                        outerRadius={80}
                                        paddingAngle={1}
                                        dataKey="value"
                                        stroke="#fff"
                                        strokeWidth={1}
                                    >
                                        {shareByType.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="clinic-dashboard-legend" style={{ flexWrap: 'wrap', justifyContent: 'center', gap: '8px 16px' }}>
                                {shareByType.map((entry, index) => (
                                    <div key={index} className="clinic-dashboard-legend-item">
                                        <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                        <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutpatientCare;
